import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { smsConsentCaptureOn, type ClientConfig } from '@ferro/config';
import {
  adminPaymentsApi,
  createCustomerDog,
  fetchCustomer,
  fetchCustomerActivity,
  fetchCustomerDogs,
  readSession,
  updateCustomer,
  humanizeApiError,
  type ActivityEntry,
  type AdminTierAuditEntry,
  type CreateDogInput,
  type CustomerDetail as CustomerDetailRecord,
  type AdminDog,
  type CustomerSummary,
  type UpdateCustomerInput,
} from '../lib/api.js';
import { Alert } from '@ferro/ui';
import { CommunicationLog } from '../components/customers/CommunicationLog.js';
// #715 — staff-only notes on the parent record.
import { CustomerNotesPanel } from '../components/customers/CustomerNotesPanel.js';
// Sawyer 11 client-notes-agreements (F1, F4) — pinned notes on every tab;
// "Signed documents" from per-document signatures.
import { PinnedNotesBand } from '../components/customers/PinnedNotesBand.js';
import { SignedDocumentsCard } from '../components/customers/SignedDocumentsCard.js';
import { NotificationPreferencesCard } from '../components/customers/NotificationPreferencesCard.js';
import { SmsConsentCard } from '../components/customers/SmsConsentCard.js';
// Ted #35 (#1190 story 35.3) — the client's agreed prices, saved once here.
import { RateAgreementsCard } from '../components/customers/RateAgreementsCard.js';
import { CustomerForm } from '../components/customers/CustomerForm.js';
import { AddDogForm } from '../components/dogs/AddDogForm.js';
import { DogCard } from '../components/dogs/DogCard.js';
// #653 — the desk create affordance is gated on `pets:write:all`, the same
// permission the server enforces. `rolesHavePermission` + `effectiveRolesOf`
// (not the single-role `roleHasPermission`) so a multi-role session (#428)
// is judged on the union, matching DogDetail's care-instructions gate.
// This is a UX gate only: showing a button that the server would 403 is
// worse than not showing it, but the server remains the authority.
import {
  effectiveRolesOf,
  rolesHavePermission,
} from '../lib/rbac-matrix.js';
import { GroomingNotesTab } from '../components/pets/GroomingNotesTab.js';
import {
  PackageBalance,
  type PackageServiceKind,
} from '../components/checkout/PackageBalance.js';
import { PackageHistory } from '../components/checkout/PackageHistory.js';
import { useCustomerPackages } from '../components/checkout/useCustomerPackages.js';

type DetailTab =
  | 'overview'
  | 'pets'
  | 'grooming'
  | 'communications'
  | 'activity';

const TABS: ReadonlyArray<{ key: DetailTab; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'pets', label: 'Pets' },
  { key: 'grooming', label: 'Grooming notes' },
  { key: 'communications', label: 'Communication log' },
  { key: 'activity', label: 'Activity' },
];

type CustomerDetailProps = {
  config: ClientConfig;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      customer: CustomerDetailRecord;
      activity: ActivityEntry[];
      dogs: AdminDog[];
    };

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// "Customer since Mar 2024" — humanized member-since, never a raw ISO string.
function formatMemberSince(iso: string): string | null {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
  } catch {
    return null;
  }
}

function titleCase(s: string): string {
  return s
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Source is an internal enum — never show it raw (anti-pattern list).
function humanizeSource(source: string): string {
  switch (source) {
    case 'organic':
      return 'Signed up online';
    case 'gingr_import':
      return 'Imported from Gingr';
    default:
      return titleCase(source);
  }
}

function activityLabel(e: ActivityEntry): string {
  switch (e.kind) {
    case 'booking':
      return `Booking · ${e.service_name ?? 'service'} · ${e.status} · ${formatMoney(
        e.total_cents,
      )}`;
    case 'redemption':
      return `Redemption · tier ${e.reward_tier} · ${e.status} · ${e.redemption_code}`;
    case 'points':
      return `Points · ${e.type} · ${e.amount > 0 ? '+' : ''}${e.amount}${
        e.description ? ` · ${e.description}` : ''
      }`;
    case 'note':
      return `Note · ${e.body}`;
  }
}

export function CustomerDetail({ config }: CustomerDetailProps): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [editOpen, setEditOpen] = useState(false);
  // #653 — the walk-in "add a pet" modal.
  const [addDogOpen, setAddDogOpen] = useState(false);
  const [tab, setTab] = useState<DetailTab>('overview');
  // Issue #33 — Communication Log can be scoped to one pet. When the user
  // picks a dog the log filters to just that animal; "all pets" clears.
  const [activeDogId, setActiveDogId] = useState<string | null>(null);
  // Issue #34 — Grooming notes are per-pet. The grooming tab forces a
  // dog selection; defaults to the first dog on load.
  const [groomingDogId, setGroomingDogId] = useState<string | null>(null);
  const token = useMemo(() => readSession().token ?? '', []);

  const apiUrl = config.deployment.apiUrl;

  // Issue #26 / #140 — package balance is server-of-record. Re-fetches
  // when the id changes so navigation between customers picks up the
  // right rows. #1157 E2.2 — a FAILED read is its own state ("couldn't
  // load … check before charging" + Retry), never the "No package on file"
  // an empty list renders: that answer sends the desk to charge a card for
  // a prepaid visit.
  const { load: packagesLoad, reload: reloadPackages } = useCustomerPackages(apiUrl, id);

  // Issue #140 (AC2) — sell/grant a day-package from the customer record.
  const [sellOpen, setSellOpen] = useState(false);
  const [sellKind, setSellKind] = useState<PackageServiceKind>('daycare');
  const [sellTotalDays, setSellTotalDays] = useState('10');
  const [sellLabel, setSellLabel] = useState('');
  // #815 — sale money: price + tender, or an explicit grant (no charge).
  const [sellPriceDollars, setSellPriceDollars] = useState('');
  const [sellTender, setSellTender] = useState<
    'cash' | 'check' | 'external_terminal' | ''
  >('');
  const [sellGrant, setSellGrant] = useState(false);
  // #1157 E2.6 — optional last day the package can be used.
  const [sellExpiresOn, setSellExpiresOn] = useState('');
  const [sellSubmitting, setSellSubmitting] = useState(false);
  const [sellError, setSellError] = useState<string | null>(null);

  // Issue #140 (AC6) — grooming price-tier change history.
  const [tierAudit, setTierAudit] = useState<AdminTierAuditEntry[]>([]);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void adminPaymentsApi
      .listTierAudit(apiUrl, { customerId: id })
      .then((res) => {
        if (!cancelled) setTierAudit(res.data?.entries ?? []);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, id]);

  const handleSellPackage = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      if (!id || sellSubmitting) return;
      const totalDays = Number.parseInt(sellTotalDays, 10);
      if (!Number.isFinite(totalDays) || totalDays <= 0) {
        setSellError('Enter a positive number of days.');
        return;
      }
      // #815 — a sale needs money facts; a grant is an explicit choice.
      const priceCents = Math.round(Number(sellPriceDollars) * 100);
      if (!sellGrant) {
        if (!Number.isFinite(priceCents) || priceCents <= 0) {
          setSellError('Enter the price the customer is paying (or mark it a grant).');
          return;
        }
        if (!sellTender) {
          setSellError('Pick how it was paid (cash / check / card terminal).');
          return;
        }
      }
      setSellSubmitting(true);
      setSellError(null);
      const trimmedLabel = sellLabel.trim();
      const res = await adminPaymentsApi.sellPackage(apiUrl, {
        customerId: id,
        serviceKind: sellKind,
        totalDays,
        label: trimmedLabel.length > 0 ? trimmedLabel : null,
        ...(sellGrant
          ? { grant: true }
          : { priceCents, paymentMethod: sellTender as 'cash' | 'check' | 'external_terminal' }),
        ...(sellExpiresOn ? { expiresOn: sellExpiresOn } : {}),
      });
      setSellSubmitting(false);
      if (res.error || !res.data) {
        setSellError(res.error ?? 'Could not sell the package. Please try again.');
        return;
      }
      // Refresh from the server-of-record + reset the form.
      await reloadPackages();
      setSellOpen(false);
      setSellKind('daycare');
      setSellTotalDays('10');
      setSellLabel('');
      setSellPriceDollars('');
      setSellTender('');
      setSellGrant(false);
      setSellExpiresOn('');
    },
    [
      apiUrl,
      id,
      sellKind,
      sellTotalDays,
      sellLabel,
      sellPriceDollars,
      sellTender,
      sellGrant,
      sellExpiresOn,
      sellSubmitting,
      reloadPackages,
    ],
  );

  const load = useCallback(async () => {
    if (!id) {
      setState({ kind: 'error', message: 'Missing customer id' });
      return;
    }
    setState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setState({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    try {
      // #715 — notes are no longer fetched here. CustomerNotesPanel owns
      // its own load so it can render its own loading/error states; a
      // notes failure must not take the whole customer page down, and the
      // page must not have to invent a loading state on its behalf.
      const [customerRes, activityRes, dogsRes] = await Promise.all([
        fetchCustomer(apiUrl, token, id),
        fetchCustomerActivity(apiUrl, token, id),
        fetchCustomerDogs(apiUrl, token, id),
      ]);
      setState({
        kind: 'ready',
        customer: customerRes.customer,
        activity: activityRes.activity,
        dogs: dogsRes.dogs,
      });
    } catch (err) {
      const message = humanizeApiError(err instanceof Error ? err.message : null);
      setState({ kind: 'error', message });
    }
  }, [apiUrl, id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Default the grooming-tab pet picker to the first dog whenever the
  // dogs list loads. Subsequent picks are user-driven.
  useEffect(() => {
    if (state.kind !== 'ready') return;
    if (state.dogs.length === 0) {
      setGroomingDogId(null);
      return;
    }
    setGroomingDogId((prev) => {
      if (prev && state.dogs.some((d) => d.id === prev)) return prev;
      return state.dogs[0]?.id ?? null;
    });
  }, [state]);

  const handleEditSubmit = async (
    input: UpdateCustomerInput,
  ): Promise<CustomerSummary> => {
    if (!id) throw new Error('Missing customer id');
    const { token } = readSession();
    if (!token) throw new Error('Not authenticated');
    const res = await updateCustomer(apiUrl, token, id, input);
    setState((current) => {
      if (current.kind !== 'ready') return current;
      return {
        ...current,
        customer: { ...current.customer, ...res.customer },
      };
    });
    return res.customer;
  };

  // LG (#1112) — the grandfathered-transport switch; one PATCH, the row updated in place.
  const [transportSaving, setTransportSaving] = useState(false);
  const [transportError, setTransportError] = useState<string | null>(null);
  // Optimistic while the PATCH is in flight (a controlled checkbox that snaps
  // back for 200ms reads as broken at the counter); reverted on failure.
  const [transportPending, setTransportPending] = useState<boolean | null>(null);
  const handleTransportGrandfathered = async (next: boolean): Promise<void> => {
    if (!id) return;
    const { token } = readSession();
    if (!token) return;
    setTransportSaving(true);
    setTransportError(null);
    setTransportPending(next);
    try {
      await updateCustomer(apiUrl, token, id, { transportGrandfathered: next });
      setState((current) => {
        if (current.kind !== 'ready') return current;
        return { ...current, customer: { ...current.customer, transport_grandfathered: next } };
      });
    } catch (err) {
      setTransportError(humanizeApiError(err instanceof Error ? err.message : null));
    } finally {
      setTransportPending(null);
      setTransportSaving(false);
    }
  };

  /**
   * #653 — save a walk-in's dog, then REFETCH rather than splicing the new
   * dog into local state. The list, the grooming-tab dog picker and the
   * communications filter all read from `state.dogs`; one refetch keeps
   * them from disagreeing, and it means the incomplete flag rendered on
   * the card is the server's derived value, never a client guess
   * (Principle 1: if two things could disagree, they come from one source).
   */
  const handleAddDog = async (input: CreateDogInput): Promise<void> => {
    if (!id) throw new Error('Missing customer id');
    const { token } = readSession();
    if (!token) throw new Error('Not authenticated');
    await createCustomerDog(apiUrl, token, id, input);
    await load();
  };

  // #653 — same permission the server enforces on POST .../dogs. A UX gate
  // only: an affordance that 403s is worse than no affordance.
  const canAddDog = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'pets:write:all',
  );

  // #715 — same shape, same permission string the route-permission map
  // enforces on POST /customers/:id/notes. A read-only role still sees the
  // note history; it just gets no compose box.
  const canWriteCustomerNotes = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'customers.notes:write:all',
  );
  // Sawyer 11 (F4) — the route map's permission for the per-document
  // standing (owner, manager, receptionist). The accountant gets no card.
  const canSeeSignedDocuments = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'contracts.customer:read:all',
  );

  // #682 — the permission the route map enforces on PATCH
  // /customers/:id/notification-preferences (receptionist+). The accountant
  // still sees the settings read-only.
  // Ted #35 (#1190 story 35.3) — saving / changing a client's agreed price is
  // an OWNER pricing decision (business-config.pricing:write:all, the same
  // gate as pricing rules). UX gate only; the server is the authority.
  const canWriteRateAgreements = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'business-config.pricing:write:all',
  );
  const canWriteNotificationPrefs = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'customers:write:all',
  );

  // The panel fetches on its own, so it needs the bearer token directly.
  const notesToken = readSession().token ?? '';

  // Ted #1 (#904) — the same permission string the route map enforces on
  // POST /packages/:id/adjust (desk write class). Read-only roles still see
  // the package history; they get no Correct-balance control.
  const canAdjustPackages = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'payments-ops:write:all',
  );
  // Past visits the desk can name on a correction — from the activity feed
  // already loaded for the page (newest first).
  const pastVisits =
    state.kind === 'ready'
      ? state.activity.flatMap((e) =>
          e.kind === 'booking'
            ? [{ id: e.id, date: e.date, serviceName: e.service_name, status: e.status }]
            : [],
        )
      : [];

  const addDogButton = canAddDog ? (
    <button
      type="button"
      className="ferro-btn ferro-btn--ghost"
      data-testid="add-dog-open"
      onClick={() => setAddDogOpen(true)}
    >
      + Add a pet
    </button>
  ) : null;

  const dogsList =
    state.kind === 'ready' ? (
      state.dogs.length === 0 ? (
        // Empty state teaches AND acts (Principle 2: dead ends are
        // defects). Before #653 this sentence told staff to do something
        // the platform gave them no way to do.
        <div className="ferro-dogs-empty">
          {/* Copy left EXACTLY as-is on purpose. It already said the right
              thing; what was missing was any way to do it. Rewording it
              would only churn another lane's assertions. */}
          <p className="ferro-muted">
            No dogs on file — add one to start booking.
          </p>
          {addDogButton}
        </div>
      ) : (
        <div className="ferro-dogs-list">
          {state.dogs.map((dog) => (
            <DogCard key={dog.id} dog={dog} />
          ))}
        </div>
      )
    ) : null;

  const packageBalanceBody =
    packagesLoad.kind !== 'ready' ? (
      <PackageBalance
        pkg={null}
        variant="pet-card"
        emptyReason={
          packagesLoad.kind === 'error'
            ? { kind: 'error', message: packagesLoad.message, onRetry: () => void reloadPackages() }
            : { kind: 'loading' }
        }
      />
    ) : packagesLoad.packages.length === 0 ? (
      <PackageBalance pkg={null} variant="pet-card" />
    ) : (
      <div className="ferro-stack">
        {packagesLoad.packages.map((pkg) => (
          <div key={pkg.id} data-testid={`package-card-${pkg.id}`}>
            <PackageBalance pkg={pkg} variant="pet-card" />
            {/* Ted #1 (#904) — every change to the balance, and the correction control. */}
            <PackageHistory
              apiUrl={apiUrl}
              pkg={pkg}
              canWrite={canAdjustPackages}
              visits={pastVisits}
              onChanged={reloadPackages}
            />
          </div>
        ))}
      </div>
    );

  return (
    <section className="ferro-admin-page" aria-busy={state.kind === 'loading'}>
      <Link to="/customers" className="ferro-back">
        ← Customers
      </Link>

      {state.kind === 'loading' ? (
        <p className="ferro-muted" role="status">
          Loading customer…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready'
        ? (() => {
            const c = state.customer;
            const memberSince = formatMemberSince(c.created_at);
            const walletCents = c.wallet_balance_cents;
            const hasWallet =
              walletCents !== undefined && walletCents !== null;
            const openInvoices = c.open_invoice_count;
            const address = [c.address1, c.address2].filter(Boolean).join(', ');
            const cityLine = [c.city, c.state, c.zip].filter(Boolean).join(' ');
            const hasAddress = Boolean(address || cityLine);
            const hasEmergency = Boolean(
              c.emergency_contact_name || c.emergency_contact_phone,
            );

            return (
              <>
                <header className="ferro-profile-header">
                  <div className="ferro-profile-header__id">
                    <h1 className="ferro-profile-header__name">{c.name}</h1>
                    <div className="ferro-profile-header__meta">
                      <a href={`mailto:${c.email}`}>{c.email}</a>
                      {c.phone ? (
                        <a href={`tel:${c.phone}`}>· {c.phone}</a>
                      ) : null}
                      {c.home_phone ? (
                        <span>
                          · <a href={`tel:${c.home_phone}`}>{c.home_phone}</a>{' '}
                          (landline)
                        </span>
                      ) : null}
                      {memberSince ? (
                        <span>· Customer since {memberSince}</span>
                      ) : null}
                    </div>
                    <div className="ferro-profile-chips">
                      <span className="ferro-profile-chip ferro-profile-chip--info">
                        {c.points_balance} pts
                      </span>
                      {hasWallet && c.wallet_tier ? (
                        <span className="ferro-profile-chip ferro-profile-chip--neutral">
                          {titleCase(c.wallet_tier)} tier
                        </span>
                      ) : null}
                      <span className="ferro-profile-chip ferro-profile-chip--neutral">
                        {humanizeSource(c.source)}
                      </span>
                      <span
                        className={`ferro-profile-chip ferro-profile-chip--${
                          c.account_status === 'active' ? 'success' : 'neutral'
                        }`}
                      >
                        {titleCase(c.account_status)}
                      </span>
                    </div>
                    {c.referred_by ? (
                      <p className="ferro-muted">
                        Referred by {c.referred_by.name}
                        {c.referrals_count > 0
                          ? ` · ${c.referrals_count} referral${
                              c.referrals_count === 1 ? '' : 's'
                            }`
                          : ''}
                      </p>
                    ) : c.referrals_count > 0 ? (
                      <p className="ferro-muted">
                        {c.referrals_count} referral
                        {c.referrals_count === 1 ? '' : 's'}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost"
                    onClick={() => setEditOpen(true)}
                    data-testid="customer-edit"
                  >
                    Edit contact info
                  </button>
                </header>

                {/* Sawyer 11 client-notes-agreements (F1) — off the Overview the
                    notes panel is not on screen, so pinned notes ride a band
                    here, above the tabs. Nothing renders when nothing is pinned. */}
                {tab !== 'overview' && id ? (
                  <PinnedNotesBand
                    apiUrl={apiUrl}
                    customerId={id}
                    refreshKey={tab}
                    onShowAllPinned={() => setTab('overview')}
                  />
                ) : null}

                <div
                  className="ferro-segmented"
                  role="tablist"
                  aria-label="Customer detail tabs"
                >
                  {TABS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      role="tab"
                      aria-selected={tab === t.key}
                      className={`ferro-segmented__btn${
                        tab === t.key ? ' ferro-segmented__btn--active' : ''
                      }`}
                      onClick={() => {
                        setTab(t.key);
                        if (t.key !== 'communications') setActiveDogId(null);
                      }}
                      data-testid={`tab-${t.key}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {tab === 'overview' ? (
                  <>
                    {/* Sawyer 11 client-notes-agreements (F1) — the notes panel is
                        the FIRST card on the Overview, above Balance: Sawyer's
                        "pin it to the top". */}
                    {/* #715 — staff-only notes on the parent. The panel owns
                        its own fetch, its own five states and the on-screen
                        privacy promise Kylie asked for; this page only tells
                        it who it is talking to. `notesToken` is non-empty
                        whenever the page rendered at all (load() bails
                        without a session), so no auth branch is needed here. */}
                    {id && notesToken ? (
                      <CustomerNotesPanel
                        apiUrl={apiUrl}
                        token={notesToken}
                        customerId={id}
                        canWrite={canWriteCustomerNotes}
                      />
                    ) : null}
                    {/* Balance strip — only what our data supports. Wallet
                        balance & open-invoice count come from the server
                        detail payload (#593). No wallet row → honest empty. */}
                    <article
                      className="ferro-dashboard__card"
                      aria-label="Balance"
                      data-testid="balance-strip-card"
                    >
                      <header className="ferro-dashboard__cardHeader">
                        <h2 className="ferro-dashboard__cardTitle">Balance</h2>
                      </header>
                      <div className="ferro-profile-strip">
                        <div className="ferro-profile-stat">
                          <span className="ferro-profile-stat__label">
                            Wallet
                          </span>
                          <span
                            className="ferro-profile-stat__value"
                            data-testid="wallet-balance"
                          >
                            {hasWallet ? formatMoney(walletCents as number) : '—'}
                          </span>
                          {!hasWallet ? (
                            <span className="ferro-profile-stat__note">
                              No wallet
                            </span>
                          ) : null}
                        </div>
                        <div className="ferro-profile-stat">
                          <span className="ferro-profile-stat__label">
                            Open invoices
                          </span>
                          <span
                            className="ferro-profile-stat__value"
                            data-testid="open-invoices"
                          >
                            {openInvoices ?? 0}
                          </span>
                        </div>
                      </div>
                    </article>

                    {/* Contact & address */}
                    <article
                      className="ferro-dashboard__card"
                      aria-label="Contact and address"
                    >
                      <header className="ferro-dashboard__cardHeader">
                        <h2 className="ferro-dashboard__cardTitle">
                          Contact &amp; address
                        </h2>
                      </header>
                      <dl className="ferro-profile-facts">
                        <div className="ferro-profile-fact">
                          <dt>Email</dt>
                          <dd>
                            <a href={`mailto:${c.email}`}>{c.email}</a>
                          </dd>
                        </div>
                        {c.phone ? (
                          <div className="ferro-profile-fact">
                            <dt>Mobile</dt>
                            <dd>
                              <a href={`tel:${c.phone}`}>{c.phone}</a>
                            </dd>
                          </div>
                        ) : null}
                        {c.home_phone ? (
                          <div className="ferro-profile-fact">
                            <dt>Landline</dt>
                            <dd>
                              <a href={`tel:${c.home_phone}`}>{c.home_phone}</a>
                            </dd>
                          </div>
                        ) : null}
                        {hasAddress ? (
                          <div className="ferro-profile-fact ferro-profile-fact--wide">
                            <dt>Mailing address</dt>
                            <dd>
                              {address ? <div>{address}</div> : null}
                              {cityLine ? <div>{cityLine}</div> : null}
                            </dd>
                          </div>
                        ) : null}
                      </dl>
                      {!hasAddress ? (
                        <p className="ferro-muted">No address on file.</p>
                      ) : null}
                    </article>

                    {/* LG (#1112, board Contracts §4) — transport is for
                        grandfathered clients only. The switch lives on the
                        customer, set from Ted's list by the desk; the Add
                        Service dialog shows a caution for anyone unmarked. */}
                    <article
                      className="ferro-dashboard__card"
                      aria-label="Transport"
                      data-testid="transport-grandfather-card"
                    >
                      <header className="ferro-dashboard__cardHeader">
                        <h2 className="ferro-dashboard__cardTitle">Transport</h2>
                      </header>
                      <p className="ferro-muted">
                        Transport is no longer offered to new clients. Mark this client as
                        grandfathered to add transport to their visits without a caution.
                      </p>
                      <label className="ferro-field ferro-field--inline" htmlFor="transport-grandfathered">
                        <input
                          id="transport-grandfathered"
                          name="transportGrandfathered"
                          type="checkbox"
                          checked={transportPending ?? c.transport_grandfathered === true}
                          disabled={!canWriteNotificationPrefs || transportSaving}
                          onChange={(e) => void handleTransportGrandfathered(e.target.checked)}
                          data-testid="transport-grandfathered"
                        />
                        <span className="ferro-field__label">Grandfathered for transport</span>
                      </label>
                      {transportError ? (
                        <p className="ferro-form__error" role="alert" data-testid="transport-grandfathered-error">
                          {transportError}
                        </p>
                      ) : null}
                      {!canWriteNotificationPrefs ? (
                        <p className="ferro-muted">Only staff who can edit customers may change this.</p>
                      ) : null}
                    </article>

                    {/* Ted #35 (#1190 story 35.3) — the client's agreed prices,
                        right after Transport: both are "this client is the
                        exception" facts the desk reads before a visit. */}
                    <RateAgreementsCard apiUrl={apiUrl} customerId={id ?? ''} canWrite={canWriteRateAgreements} />

                    {/* Emergency contact */}
                    <article
                      className="ferro-dashboard__card"
                      aria-label="Emergency contact"
                    >
                      <header className="ferro-dashboard__cardHeader">
                        <h2 className="ferro-dashboard__cardTitle">
                          Emergency contact
                        </h2>
                      </header>
                      {hasEmergency ? (
                        <dl className="ferro-profile-facts">
                          {c.emergency_contact_name ? (
                            <div className="ferro-profile-fact">
                              <dt>Name</dt>
                              <dd>{c.emergency_contact_name}</dd>
                            </div>
                          ) : null}
                          {c.emergency_contact_phone ? (
                            <div className="ferro-profile-fact">
                              <dt>Phone</dt>
                              <dd>
                                <a href={`tel:${c.emergency_contact_phone}`}>
                                  {c.emergency_contact_phone}
                                </a>
                              </dd>
                            </div>
                          ) : null}
                        </dl>
                      ) : (
                        <p className="ferro-muted">
                          No emergency contact on file — collect at intake.
                        </p>
                      )}
                    </article>

                    {/* Communication preferences — only the flags we store */}
                    <article
                      className="ferro-dashboard__card"
                      aria-label="Communication preferences"
                    >
                      <header className="ferro-dashboard__cardHeader">
                        <h2 className="ferro-dashboard__cardTitle">
                          Communication preferences
                        </h2>
                      </header>
                      <div className="ferro-prefs">
                        <span
                          className={`ferro-profile-chip ferro-profile-chip--${
                            c.sms_deals_opted_out ? 'neutral' : 'success'
                          }`}
                        >
                          SMS deals:{' '}
                          {c.sms_deals_opted_out ? 'opted out' : 'opted in'}
                        </span>
                        <span
                          className={`ferro-profile-chip ferro-profile-chip--${
                            c.opt_out_photo_sharing ? 'neutral' : 'success'
                          }`}
                        >
                          Photo sharing:{' '}
                          {c.opt_out_photo_sharing ? 'opted out' : 'opted in'}
                        </span>
                        <span
                          className={`ferro-profile-chip ferro-profile-chip--${
                            c.opt_out_rewards ? 'neutral' : 'success'
                          }`}
                        >
                          Rewards: {c.opt_out_rewards ? 'opted out' : 'opted in'}
                        </span>
                      </div>
                    </article>

                    {/* #682 — which emails this customer gets, and who set it. */}
                    {id && notesToken ? (
                      <NotificationPreferencesCard
                        apiUrl={apiUrl}
                        token={notesToken}
                        customerId={id}
                        canWrite={canWriteNotificationPrefs}
                      />
                    ) : null}

                    {/* #1212 B2a — whether this client said yes to texts, when
                        and how; the desk can record a STOP, never a yes. Shown
                        only for a tenant that captures texting consent. */}
                    {id && smsConsentCaptureOn(config) ? (
                      <SmsConsentCard
                        apiUrl={apiUrl}
                        customerId={id}
                        canWrite={canWriteNotificationPrefs}
                      />
                    ) : null}

                    <article
                      className="ferro-dashboard__card"
                      aria-label="Package balance"
                      data-testid="package-balance-card"
                    >
                      <header className="ferro-dashboard__cardHeader ferro-card__headerRow">
                        <h2 className="ferro-dashboard__cardTitle">
                          Package balance
                        </h2>
                        <button
                          type="button"
                          className="ferro-btn ferro-btn--ghost"
                          onClick={() => {
                            setSellOpen((v) => !v);
                            setSellError(null);
                          }}
                          aria-expanded={sellOpen}
                          data-testid="sell-package-toggle"
                        >
                          {sellOpen ? 'Cancel' : 'Sell package'}
                        </button>
                      </header>
                      {sellOpen ? (
                        <form
                          className="ferro-form ferro-stack"
                          onSubmit={(e) => void handleSellPackage(e)}
                          data-testid="sell-package-form"
                        >
                          {sellError ? (
                            <Alert variant="error">{sellError}</Alert>
                          ) : null}
                          <label className="ferro-field">
                            <span className="ferro-field__label">Service</span>
                            <select
                              className="ferro-field__input"
                              value={sellKind}
                              onChange={(e) =>
                                setSellKind(e.target.value as PackageServiceKind)
                              }
                              data-testid="sell-package-kind"
                            >
                              <option value="daycare">Daycare (full day)</option>
                              <option value="daycare_half">Half-day daycare</option>
                              <option value="boarding">Boarding</option>
                              <option value="grooming">Grooming</option>
                            </select>
                          </label>
                          <label className="ferro-field">
                            <span className="ferro-field__label">Total days</span>
                            <input
                              className="ferro-field__input"
                              type="number"
                              min={1}
                              inputMode="numeric"
                              value={sellTotalDays}
                              onChange={(e) => setSellTotalDays(e.target.value)}
                              data-testid="sell-package-days"
                            />
                          </label>
                          {!sellGrant ? (
                            <>
                              <label className="ferro-field">
                                <span className="ferro-field__label">
                                  Price (dollars)
                                </span>
                                <input
                                  className="ferro-field__input"
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  inputMode="decimal"
                                  value={sellPriceDollars}
                                  placeholder="450.00"
                                  onChange={(e) => setSellPriceDollars(e.target.value)}
                                  data-testid="sell-package-price"
                                />
                              </label>
                              <label className="ferro-field">
                                <span className="ferro-field__label">How paid</span>
                                <select
                                  className="ferro-field__input"
                                  value={sellTender}
                                  onChange={(e) =>
                                    setSellTender(
                                      e.target.value as
                                        | 'cash'
                                        | 'check'
                                        | 'external_terminal'
                                        | '',
                                    )
                                  }
                                  data-testid="sell-package-tender"
                                >
                                  <option value="">Choose…</option>
                                  <option value="cash">Cash</option>
                                  <option value="check">Check</option>
                                  <option value="external_terminal">Card terminal</option>
                                </select>
                              </label>
                            </>
                          ) : null}
                          <label className="ferro-field ferro-field--inline">
                            <input
                              type="checkbox"
                              checked={sellGrant}
                              onChange={(e) => setSellGrant(e.target.checked)}
                              data-testid="sell-package-grant"
                            />
                            <span className="ferro-field__label">
                              Grant — no charge (comp / adjustment)
                            </span>
                          </label>
                          <label className="ferro-field">
                            <span className="ferro-field__label">
                              Expires (optional) — the last day it can be used
                            </span>
                            <input
                              className="ferro-field__input"
                              type="date"
                              value={sellExpiresOn}
                              onChange={(e) => setSellExpiresOn(e.target.value)}
                              data-testid="sell-package-expires"
                            />
                          </label>
                          <label className="ferro-field">
                            <span className="ferro-field__label">
                              Label (optional)
                            </span>
                            <input
                              className="ferro-field__input"
                              type="text"
                              value={sellLabel}
                              placeholder="e.g. Loyalty Daycare Bundle"
                              onChange={(e) => setSellLabel(e.target.value)}
                              data-testid="sell-package-label"
                            />
                          </label>
                          <button
                            type="submit"
                            className="ferro-btn ferro-btn--primary"
                            disabled={sellSubmitting}
                            data-testid="sell-package-submit"
                          >
                            {sellSubmitting
                              ? 'Saving…'
                              : sellGrant
                                ? 'Grant package'
                                : 'Sell package'}
                          </button>
                        </form>
                      ) : null}
                      {packageBalanceBody}
                    </article>

                    <article
                      className="ferro-dashboard__card"
                      aria-label="Grooming price-tier history"
                      data-testid="tier-audit-card"
                    >
                      <header className="ferro-dashboard__cardHeader">
                        <h2 className="ferro-dashboard__cardTitle">
                          Grooming price-tier history
                        </h2>
                      </header>
                      {tierAudit.length === 0 ? (
                        <p className="ferro-muted" data-testid="tier-audit-empty">
                          No tier changes recorded for this customer.
                        </p>
                      ) : (
                        <ul
                          className="ferro-table"
                          aria-label="Tier change history"
                          data-testid="tier-audit-list"
                        >
                          {tierAudit.map((entry) => {
                            const deltaCents =
                              entry.finalTotalCents - entry.originalTotalCents;
                            const deltaLabel = `${
                              deltaCents >= 0 ? '+' : '−'
                            }${formatMoney(Math.abs(deltaCents))}`;
                            return (
                              <li
                                key={entry.id}
                                className="ferro-table__row"
                                data-testid={`tier-audit-row-${entry.id}`}
                              >
                                <div className="ferro-table__body">
                                  <div className="ferro-table__primary">
                                    {entry.originalTierLabel} →{' '}
                                    {entry.finalTierLabel} · {deltaLabel}
                                  </div>
                                  <div className="ferro-table__meta">
                                    {entry.confirmedByStaffUsername ??
                                      entry.confirmedByStaffId ??
                                      'Unknown staff'}{' '}
                                    · {formatDate(entry.confirmedAt)}
                                    {entry.operatorNotes
                                      ? ` · ${entry.operatorNotes}`
                                      : ''}
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </article>

                    <article
                      className="ferro-dashboard__card ferro-dogs-section"
                      aria-label="Dogs"
                    >
                      <header className="ferro-dashboard__cardHeader">
                        <h2 className="ferro-dashboard__cardTitle">
                          Dogs ({state.dogs.length})
                        </h2>
                        {state.dogs.length > 0 ? addDogButton : null}
                      </header>
                      {dogsList}
                    </article>

                    {/* Sawyer 11 client-notes-agreements (F4) — replaces the
                        "Agreements" card (#593), which read the login re-ack
                        column and so called a client who signed every
                        document while booking unsigned. Last on the Overview,
                        collapsed: paperwork no longer poses as a note. */}
                    {id && notesToken && canSeeSignedDocuments ? (
                      <SignedDocumentsCard apiUrl={apiUrl} token={notesToken} customerId={id} />
                    ) : null}
                  </>
                ) : null}

                {tab === 'pets' ? (
                  <>
                    <article
                      className="ferro-dashboard__card"
                      aria-label="Package balance for pets"
                      data-testid="package-balance-pets-card"
                    >
                      <header className="ferro-dashboard__cardHeader">
                        <h2 className="ferro-dashboard__cardTitle">
                          Package balance
                        </h2>
                      </header>
                      {packageBalanceBody}
                    </article>

                    <article
                      className="ferro-dashboard__card ferro-dogs-section"
                      aria-label="Pets"
                    >
                      <header className="ferro-dashboard__cardHeader">
                        <h2 className="ferro-dashboard__cardTitle">
                          Pets ({state.dogs.length})
                        </h2>
                        {/* #653 — hidden when the list is empty: the empty
                            state already carries the action, and two
                            identical primary affordances on one screen is
                            an anti-pattern. */}
                        {state.dogs.length > 0 ? addDogButton : null}
                      </header>
                      {dogsList}
                    </article>
                  </>
                ) : null}

                {tab === 'grooming' ? (
                  <>
                    {state.dogs.length === 0 ? (
                      <p className="ferro-muted">
                        Add a pet on file before logging grooming notes.
                      </p>
                    ) : (
                      <>
                        <div
                          className="ferro-segmented"
                          role="tablist"
                          aria-label="Grooming pet"
                        >
                          {state.dogs.map((d) => (
                            <button
                              key={d.id}
                              type="button"
                              role="tab"
                              aria-selected={groomingDogId === d.id}
                              className={`ferro-segmented__btn${
                                groomingDogId === d.id
                                  ? ' ferro-segmented__btn--active'
                                  : ''
                              }`}
                              onClick={() => setGroomingDogId(d.id)}
                              data-testid={`grooming-pet-${d.id}`}
                            >
                              {d.name}
                            </button>
                          ))}
                        </div>
                        {groomingDogId ? (
                          <GroomingNotesTab
                            apiUrl={apiUrl}
                            token={token}
                            dogId={groomingDogId}
                            dogName={
                              state.dogs.find((d) => d.id === groomingDogId)
                                ?.name ?? ''
                            }
                          />
                        ) : null}
                      </>
                    )}
                  </>
                ) : null}

                {tab === 'communications' ? (
                  <>
                    {state.dogs.length > 0 ? (
                      <div
                        className="ferro-segmented"
                        role="tablist"
                        aria-label="Communication scope"
                      >
                        <button
                          type="button"
                          role="tab"
                          aria-selected={activeDogId === null}
                          className={`ferro-segmented__btn${
                            activeDogId === null
                              ? ' ferro-segmented__btn--active'
                              : ''
                          }`}
                          onClick={() => setActiveDogId(null)}
                          data-testid="comm-scope-all"
                        >
                          All
                        </button>
                        {state.dogs.map((d) => (
                          <button
                            key={d.id}
                            type="button"
                            role="tab"
                            aria-selected={activeDogId === d.id}
                            className={`ferro-segmented__btn${
                              activeDogId === d.id
                                ? ' ferro-segmented__btn--active'
                                : ''
                            }`}
                            onClick={() => setActiveDogId(d.id)}
                            data-testid={`comm-scope-${d.id}`}
                          >
                            {d.name}
                          </button>
                        ))}
                      </div>
                    ) : null}

                    {id ? (
                      <CommunicationLog
                        apiUrl={apiUrl}
                        token={token}
                        customerId={id}
                        dogId={activeDogId ?? undefined}
                      />
                    ) : null}
                  </>
                ) : null}

                {tab === 'activity' ? (
                  <article className="ferro-dashboard__card" aria-label="Activity">
                    <header className="ferro-dashboard__cardHeader">
                      <h2 className="ferro-dashboard__cardTitle">Activity</h2>
                      <span className="ferro-dashboard__metric">
                        {state.activity.length}
                      </span>
                    </header>
                    {state.activity.length === 0 ? (
                      <p className="ferro-muted">No activity yet.</p>
                    ) : (
                      <ul
                        className="ferro-timeline"
                        aria-label="Activity timeline"
                      >
                        {state.activity.map((entry) => (
                          <li
                            key={`${entry.kind}-${entry.id}`}
                            className="ferro-timeline__item"
                          >
                            <span className="ferro-timeline__when">
                              {formatDate(entry.at)}
                            </span>
                            <span className="ferro-timeline__what">
                              {activityLabel(entry)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                ) : null}
              </>
            );
          })()
        : null}

      {state.kind === 'ready' ? (
        <CustomerForm
          isOpen={editOpen}
          mode={{ kind: 'edit', customer: state.customer }}
          onClose={() => setEditOpen(false)}
          onSubmitUpdate={handleEditSubmit}
        />
      ) : null}

      {/* #653 — walk-in pet capture. Only mounted for roles the server
          would actually accept a write from. */}
      {state.kind === 'ready' && canAddDog ? (
        <AddDogForm
          isOpen={addDogOpen}
          onClose={() => setAddDogOpen(false)}
          customerName={`${state.customer.first_name} ${state.customer.last_name}`.trim()}
          onSubmit={handleAddDog}
        />
      ) : null}
    </section>
  );
}
